/**
 * playerSlug.js — Centralized player slug generation with mlbId disambiguation.
 * 
 * When multiple MLB players share the same name (e.g., Will Smith the catcher 
 * vs Will Smith the pitcher), their index entries are disambiguated with the
 * mlbId suffix: "will-smith-669257" vs "will-smith-519293".
 * 
 * Use `makePlayerSlug(name, mlbId)` everywhere instead of hand-rolling slugs.
 * If mlbId is null/undefined the bare slug is returned (works for non-conflicting names).
 */

/**
 * Convert a player name to a URL-friendly slug.
 * @param {string} name — e.g. "Will Smith"
 * @returns {string} e.g. "will-smith"
 */
export const nameToSlug = (name) =>
  (name || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

/**
 * Build the player profile URL slug, appending mlbId ONLY when explicitly
 * needed (i.e. for known disambiguated players like Will Smith).
 * For most players, pass null/undefined for mlbId so the bare slug is used.
 * @param {string} name — player display name
 * @param {string|number|null} mlbId — MLB ID for disambiguation (only pass for known conflicts)
 * @returns {string} e.g. "will-smith-669257" or "jose-ramirez"
 */
export const makePlayerSlug = (name, mlbId) => {
  const slug = nameToSlug(name);
  return mlbId ? `${slug}-${mlbId}` : slug;
};

/**
 * Build the full profile URL path.
 * @param {string} name
 * @param {string|number|null} mlbId
 * @returns {string} e.g. "/players/profile/will-smith-669257"
 */
export const playerProfileUrl = (name, mlbId) =>
  `/players/profile/${makePlayerSlug(name, mlbId)}`;
